// Copyright (C) 2022 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import React, { useEffect, useRef, useState } from "react"
import { IonInput, IonItem } from "@ionic/react"
import { IFieldValidator, NullFieldValidator } from "./IFieldValidator"
import "./IonItemInput.scss"


interface IonItemInputProps extends React.ComponentProps<typeof IonItem> {
  enterkeyHint?: string
  error?: string | null
  helpText?: string
  inputmode?: string
  inputComponentProps?: any
  inputType?: "date" | "datetime-local" | "email" | "month" | "number" | "password" | "search" | "tel" | "text" | "time" | "url" | "week"
  label?: string
  maxLength?: number
  onReturn?: (value: string) => void
  onValidated(isValid: boolean, value: string): void
  readonly?: boolean
  successMessage?: string | React.ReactNode
  transformers?: { (value: string): string } []
  validator?: IFieldValidator
  value?: any
}


const IonItemInput: React.FC<IonItemInputProps> = ({
  error,
  helpText,
  inputComponentProps,
  inputType,
  label,
  maxLength,
  onValidated,
  placeholder,
  readonly,
  style,
  successMessage,
  validator,
  value,
  ...props
}) => {
  const classNames: string[] = [""]
  const inputEl = useRef<HTMLIonInputElement>(null)
  const inputProps = inputComponentProps || {}
  const inputValidator = validator || (new NullFieldValidator())
  const [currrentValue, setCurrentValue] = useState<string | null>(value || null)
  const [hasFocus, setHasFocus] = useState<boolean>(false)
  const [touched, setTouched] = useState<boolean>(false)
  const [valid, setValid] = useState<boolean>(false)
  const [validatorMessage, setValidatorMessage] = useState<string | null | React.ReactNode>(null)
  const errorMessage = error || validatorMessage
  const transformers = props.transformers || []

  const onInput = (e: Event) => {
    const value = (e.target as HTMLIonInputElement).value as string;
    const cleaned = transformers.reduce((v, f) => f(v), value)
    setCurrentValue(cleaned)
    const inputCmp = inputEl.current;
    if (inputCmp !== null) {
      inputCmp.value = cleaned;
    }
  }

  useEffect(() => {
    if (currrentValue === null) {
      return
    }
    const errorMessages = inputValidator.validate(currrentValue)
    setValidatorMessage(errorMessages[0] || null)
    setValid(errorMessages.length === 0)
  }, [currrentValue])

  useEffect(() => {
    onValidated(valid, currrentValue || "")
  }, [valid, currrentValue])

  if (!!errorMessage) {
    classNames.push("ion-invalid")
    classNames.push("ion-touched")
  }
  if (!!successMessage && valid && hasFocus) {
    classNames.push("ion-complete")
  }

  useEffect(() => {
    if (value === null) {
      setCurrentValue(null)
    }
  }, [value])
  return (
    <IonItem {...{...props, lines: "none"}} style={style}>
      <IonInput
        autocapitalize={props.autoCapitalize}
        autocomplete="off"
        autocorrect="off"
        ariaLabel={label}
        className={classNames.join(" ")} 
        clearInput
        enterkeyhint={props.enterkeyHint}
        errorText={errorMessage}
        helperText={(!!valid && !!successMessage && hasFocus) ? successMessage : helpText || ""}
        inputmode={props.inputmode}
        label={label}
        labelPlacement="stacked"
        maxlength={maxLength}
        onKeyDown={(e: KeyboardEvent) => {
          if (["Enter", "NumpadEnter"].includes(e.code)) {
            e.preventDefault()
            if (valid && props.onReturn) {
              props.onReturn(value)
            }
          }
        }}
        onIonInput={onInput}
        onIonBlur={() => setHasFocus(false)}
        onIonFocus={() => setHasFocus(true)}
        placeholder={placeholder}
        readonly={readonly}
        ref={inputEl}
        type={inputType || "text"}
        value={currrentValue}
        {...inputProps}
      />
    </IonItem>
  )
}


export { IonItemInput }